import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { buildProductSpecRows } from '@/experiences/sales-portal/domain/items/blocs/transformers/product-spec-rows-transformer';
import { createStyles } from '@/contexts/theme-context';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { ItemDetailContentHeader } from '@/experiences/sales-portal/domain/items/components/item-detail-content-header';
import { ItemDetailDetailsLoadingSkeleton } from '@/experiences/sales-portal/domain/items/components/details-tab/details-tab-loading-skeleton';
import { ItemDetailEmptyState } from '@/experiences/sales-portal/domain/items/components/item-detail-empty-state';
import { ItemDetailItemInfo } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailSection } from '@/experiences/sales-portal/domain/items/components/item-detail-section';
import { ProductDescription } from '@/experiences/sales-portal/domain/items/components/details-tab/product-description';
import { PurchaseHistoryList } from '@/experiences/sales-portal/domain/items/components/purchase-history/purchase-history-list';
import { ScrollView, View } from 'react-native';
import { SidePanel } from '@usepepper/sprout-kit-ui';
import { useIsGrossProfitHidden } from '@/experiences/sales-portal/blocs/hooks/use-is-gross-profit-hidden';
import { useItemDetailLoader } from '@/experiences/sales-portal/domain/items/blocs/loaders/use-item-detail-loader';

export interface ItemDetailDrawerProps {
  /** Extra space below the scrolled content, for hosts that sit over a safe area. */
  bottomInset?: number;
  /**
   * Omits the header's leading close action.
   * See {@link ItemDetailContentHeaderProps.hideAction}.
   */
  hideHeaderAction?: boolean;
  itemInfo: ItemDetailItemInfo;
  onClose: () => void;
  restaurantUUID: string;
  supplierUUID: string;
  timezone: string;
  variantPackUUID: string;
}

export const ItemDetailDrawer: React.FC<ItemDetailDrawerProps> = ({
  bottomInset = 0,
  hideHeaderAction = false,
  itemInfo,
  onClose,
  restaurantUUID,
  supplierUUID,
  timezone,
  variantPackUUID,
}) => {
  const [styles] = useStyles();
  const isGrossProfitHidden = useIsGrossProfitHidden(supplierUUID);

  const { error, loading, purchaseHistory, refetch, saltInfo } =
    useItemDetailLoader({
      restaurantUUID,
      supplierUUID,
      timezone,
      variantPackUUID,
    });

  const description =
    saltInfo?.description?.trim() || itemInfo.description?.trim() || null;

  const productSpecRows = useMemo(
    () => buildProductSpecRows(saltInfo, itemInfo),
    [saltInfo, itemInfo],
  );

  const isEmpty =
    purchaseHistory.length === 0 &&
    productSpecRows.length === 0 &&
    !description;

  return (
    <View style={styles.container}>
      <ItemDetailContentHeader
        externalItemId={itemInfo.externalItemId}
        hideAction={hideHeaderAction}
        imageUrl={itemInfo.imageUrl}
        itemName={itemInfo.name}
        onClose={onClose}
        packDisplayString={itemInfo.packDisplayString}
      />
      <SidePanel.Body {...(error ? {} : { paddingTop: 0 })}>
        {error ? (
          <ErrorState
            description={i18n.t(
              'sales_portal.order_entry.item_detail.error.description',
            )}
            onPress={refetch}
            title={i18n.t('sales_portal.order_entry.item_detail.error.title')}
          />
        ) : loading ? (
          <ItemDetailDetailsLoadingSkeleton />
        ) : isEmpty ? (
          <ItemDetailEmptyState itemName={itemInfo.name} />
        ) : (
          <ScrollView
            contentContainerStyle={[
              styles.detailsBody,
              { paddingBottom: bottomInset },
            ]}
            style={styles.detailsScroll}
          >
            <ProductDescription description={description} />
            <ItemDetailSection
              content={{ kind: 'rows', rows: productSpecRows }}
              title={i18n.t(
                'sales_portal.order_entry.item_detail.product_specs.title',
              )}
            />
            <PurchaseHistoryList
              entries={purchaseHistory}
              hideGpField={isGrossProfitHidden}
            />
          </ScrollView>
        )}
      </SidePanel.Body>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceMedium,
    textAlign: 'left',
  },
  detailsBody: {
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  detailsScroll: {
    flex: 1,
  },
}));
